/**
 * /admin/produtos/universal — catálogo único de produtos (R-IMPORT 3 A1.10).
 *
 * Mostra cada produto uma vez só, com os CNPJs em que ele existe, e permite
 * aprovar de uma vez as ligações que ainda esperam revisão.
 *
 * FASE-C (redesign): "Produtos Universal (cross-CNPJ)", "Camada 5" e
 * `aprovacao_pendente=1` saíram da tela; os botões `orange-500`/`blue-500`
 * (contraste 2.8:1 e 3.68:1, reprovados) viraram `<Button>`/`<Badge>` de
 * token; o `<table>` cru dentro de `bg-white` (estourava +346px no celular)
 * virou `<Table>` canônico, que rola dentro do próprio card; `confirm()` e
 * `alert()` nativos viraram ConfirmDialog e Toast canônicos.
 */
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { api } from '@/lib/api';

interface UniversalItem {
  id: string;
  descricao_canonica: string;
  codigo_canonico: string | null;
  ncm: string | null;
  unidade: string | null;
  ativo: number;
  criado_em_iso: string;
  empresas_vinculadas: string | null;
  num_links: number;
  pendentes: number;
}

interface UniversalResp {
  items: UniversalItem[];
  total: number;
  limit: number;
  offset: number;
}

export function ProdutosUniversalPage() {
  const [data, setData] = useState<UniversalResp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [offset, setOffset] = useState(0);
  const confirmar = useConfirmDialog();
  const { show } = useToast();
  const limit = 100;

  function load() {
    setErr(null);
    api
      .get<UniversalResp>(`/api/v1/admin/produtos/universal?limit=${limit}&offset=${offset}`)
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(load, [offset]);

  async function approveAllPendentes() {
    const ok = await confirmar({
      title: 'Aprovar todas as ligações pendentes?',
      message:
        'Todos os produtos que o sistema juntou automaticamente e ainda esperam revisão passam a valer no catálogo único.',
      confirmLabel: 'Aprovar todas',
    });
    if (!ok) return;
    setApproving(true);
    try {
      const r = await api.post<{ aprovados: number }>(
        '/api/v1/admin/produtos/aprovar-pendentes',
        {},
      );
      show(`${r.aprovados} ligação(ões) aprovada(s).`);
      load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setApproving(false);
    }
  }

  return (
    <div className="min-w-0 space-y-5" data-testid="produtos-universal-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Catálogo único de produtos"
        subtitle="Um mesmo produto costuma estar cadastrado em mais de um CNPJ. Aqui ele aparece uma vez só, com a lista de CNPJs em que existe."
        actions={
          <Button
            onClick={approveAllPendentes}
            loading={approving}
            data-testid="approve-all-btn"
          >
            Aprovar ligações pendentes
          </Button>
        }
      />

      <Alert variant="info" title="Por que existem ligações pendentes">
        Quando o sistema junta dois cadastros por conta própria, as primeiras 50 ligações
        ficam esperando alguém confirmar antes de valer. É uma trava de segurança pra não
        misturar produtos parecidos sem revisão.
      </Alert>

      {err && (
        <Alert variant="danger" title="Não foi possível carregar o catálogo">
          Tente recarregar a página. Se continuar, avise quem cuida do servidor.
          <span className="mt-1 block break-words text-xs opacity-80">Detalhe: {err}</span>
        </Alert>
      )}

      {!data ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : data.items.length === 0 ? (
        <Card variant="flat" data-testid="empty-state">
          <EmptyState
            title="O catálogo único ainda está vazio"
            description="Sincronize os produtos do Omie pra que o sistema comece a identificar quais cadastros são o mesmo produto."
          />
        </Card>
      ) : (
        <>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3">
            {data.total} produto(s) no total — mostrando {data.items.length}.
          </p>

          <Card variant="flat" className="min-w-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead>Código</TableHead>
                  <TableHead>NCM</TableHead>
                  <TableHead>CNPJs em que existe</TableHead>
                  <TableHead>Situação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => (
                  <TableRow key={it.id} data-testid={`universal-${it.id}`}>
                    <TableCell className="min-w-[180px] break-words font-medium">
                      {it.descricao_canonica}
                    </TableCell>
                    <TableCell className="break-words font-mono text-xs">
                      {it.codigo_canonico ?? '—'}
                    </TableCell>
                    <TableCell className="break-words font-mono text-xs">
                      {it.ncm ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(it.empresas_vinculadas ?? '')
                          .split(',')
                          .filter(Boolean)
                          .map((emp, i) => (
                            <Badge key={i} variant="info" size="sm">
                              {emp}
                            </Badge>
                          ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {it.pendentes > 0 ? (
                        <Badge variant="warning" size="sm">
                          {it.pendentes} esperando revisão
                        </Badge>
                      ) : (
                        <Badge variant="success" size="sm">
                          Aprovado
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ChevronLeft className="h-4 w-4" />}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
            >
              Anteriores
            </Button>
            <Button
              variant="outline"
              size="sm"
              rightIcon={<ChevronRight className="h-4 w-4" />}
              onClick={() => setOffset(offset + limit)}
              disabled={offset + data.items.length >= data.total}
            >
              Próximos
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
